import { useEffect, useState } from 'react';
import { FileText, Loader2, User, X } from 'lucide-react';
import api from '../services/api';

/**
 * Formats a stored calendar day without shifting it across time zones.
 */
function formatDate(value) {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/**
 * Formats a CPF or CNPJ from the local Guaibim row.
 */
function formatDocument(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value || '—';
}

/**
 * Formats a stored assistance or product amount in reais.
 */
function formatMoney(value) {
  if (value == null || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return amount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Loads one Guaibim Resolve policy from the local base and shows it in a right-hand panel.
 */
function GuaibimPolicyDrawer({ customer, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [policy, setPolicy] = useState(null);

  useEffect(() => {
    if (!customer?.policyNumber) return undefined;
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      setPolicy(null);
      try {
        const response = await api.get(`/product-customers/guaibim/${encodeURIComponent(customer.policyNumber)}`);
        if (!active) return;
        setPolicy(response.data?.policy || null);
        if (!response.data?.policy) {
          setError('A base local não devolveu dados para esta apólice.');
        }
      } catch (requestError) {
        if (!active) return;
        setError(requestError.response?.data?.error || 'Erro ao consultar a apólice na base local');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [customer]);

  useEffect(() => {
    if (!customer) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [customer, onClose]);

  if (!customer) return null;

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Fechar consulta"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="guaibim-drawer-title"
        className="relative flex h-full w-full max-w-md flex-col bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Base local</p>
            <h2 id="guaibim-drawer-title" className="text-lg font-semibold text-gray-900">
              {customer.customerName || 'Cliente sem nome'}
            </h2>
            <p className="text-sm text-gray-600">Apólice {customer.policyNumber}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {loading && (
            <p className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              Consultando a apólice na base local...
            </p>
          )}

          {error && (
            <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">{error}</p>
          )}

          {policy && (
            <>
              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Apólice
                </h3>
                <Detail label="Número" value={policy.policyNumber} />
                <Detail label="Situação" value={policy.statusLabel} />
                <Detail label="Movimento" value={`${policy.operationLabel} em ${formatDate(policy.eventDate)}`} />
                <Detail label="Arquivo" value={policy.sourceName} />
              </section>

              <section className="space-y-2">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <User className="h-4 w-4 text-gray-500" />
                  Segurado
                </h3>
                <Detail label="Nome" value={policy.customerName} />
                <Detail label="CPF/CNPJ" value={formatDocument(policy.document)} />
                <Detail label="Tipo" value={policy.personType} />
                <Detail label="Nascimento" value={formatDate(policy.birthDate)} />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-800">Produto</h3>
                <Detail label="Descrição" value={policy.productDescription} />
                <Detail label="Quantidade" value={policy.quantity} />
                <Detail label="Preço do produto" value={formatMoney(policy.productPrice)} />
                <Detail label="Assistência" value={formatMoney(policy.assistancePrice)} />
                <Detail label="Valor do seguro" value={formatMoney(policy.insuranceValue)} />
                <Detail label="Valor total do seguro" value={formatMoney(policy.insuranceTotal)} />
              </section>

              <section className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-800">Nota</h3>
                <Detail label="Número" value={policy.invoiceNumber} />
                <Detail label="Data da nota" value={formatDate(policy.invoiceDate)} />
                <Detail label="Cancelamento" value={policy.cancelDate ? formatDate(policy.cancelDate) : 'Sem cancelamento'} />
                <Detail label="Nota de cancelamento" value={formatDate(policy.cancelInvoiceDate)} />
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

/**
 * Renders one labeled value inside the local policy panel.
 */
function Detail({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
    </div>
  );
}

export default GuaibimPolicyDrawer;
